import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import * as XLSX from 'xlsx';

const TargetTracker = () => {
  const { tenant, user } = useAuth();
  const isSubDistAdmin = user?.role === 'subdist_admin';
  const mySubCode = user?.sub_dist_code||'';
  const primary = tenant?.branding?.primary_color || '#1B6B3A';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSubs, setExpandedSubs] = useState({});
  const [dailyData, setDailyData] = useState({});
  const [dailyLoading, setDailyLoading] = useState({});
  const [showToday, setShowToday] = useState(false);
  const [activeTab, setActiveTab] = useState('target'); // target | range
  const [rangeFrom, setRangeFrom] = useState(new Date(new Date().getFullYear(),new Date().getMonth(),1).toISOString().split('T')[0]);
  const [rangeTo, setRangeTo] = useState(new Date().toISOString().split('T')[0]);
  const [rangePreset, setRangePreset] = useState('month');
  const [rangeData, setRangeData] = useState(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [expandedRangeSub, setExpandedRangeSub] = useState({});
  const [selectedSub, setSelectedSub] = useState('all');
  const [rangeTypeFilter, setRangeTypeFilter] = useState('all'); // all|new|retention|renewal|disconnected
  const [userModal, setUserModal] = useState(null); // {type, label, color}
  const [userList, setUserList] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const loadUsers = async (type, label, color) => {
    setUserModal({type, label, color});
    setUserList([]);
    setUserSearch('');
    setUserLoading(true);
    try {
      const r = await api.get('/reports/target-range-users', {
        params:{from_date:rangeFrom, to_date:rangeTo, type, sub:selectedSub}
      });
      if(r.data.success) setUserList(r.data.users||[]);
    } catch(e){}
    setUserLoading(false);
  }; // 'all' or specific sub code

  const loadRange = async (from, to) => {
    setRangeLoading(true);
    try {
      const r = await api.get('/reports/target-range', {params:{from_date:from||rangeFrom, to_date:to||rangeTo}});
      if(r.data.success) setRangeData(r.data);
    } catch(e){}
    setRangeLoading(false);
  };

  const exportRangeExcel = () => {
    if(!rangeData) return;
    const filteredSubs = selectedSub==='all' ? rangeData.subs : rangeData.subs.filter(s=>s.sub===selectedSub);
    const subSuffix = selectedSub==='all'?'All Sub-Districts':selectedSub;

    const wb = XLSX.utils.book_new();

    // Sheet 1: Summary
    const s1 = [
      ['PDIC Broadband - Connection Report'],
      [`Period: ${rangeFrom} to ${rangeTo}  |  Sub-District: ${subSuffix}`],
      [`Generated: ${new Date().toLocaleString('en-IN')}`],
      [],
      ['OVERALL SUMMARY'],
      ['Start Active', 'End Active', 'New Activations', 'Net Growth'],
      [rangeData.overall.start_active, rangeData.overall.end_active, rangeData.overall.total_new, rangeData.overall.net],
      [],
      ['SUB-DISTRICT SUMMARY'],
      ['Sub-District', 'Start Active', 'End Active', 'New Connections', 'Retention', 'Renewals', 'Disconnected', 'Net Growth', 'Growth %'],
      ...filteredSubs.map(s=>[
        s.sub, s.start_active, s.end_active, s.total_new, s.total_retention, s.total_renewal,
        s.daily.reduce((a,d)=>a+(d.left_active||0),0),
        s.net,
        s.start_active>0?+((s.net/s.start_active)*100).toFixed(2):0
      ])
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(s1);
    ws1['!cols']=[{wch:22},{wch:15},{wch:15},{wch:18},{wch:13},{wch:12}];
    // Style title row
    ws1['A1'] = {v:'PDIC Broadband - Connection Report', t:'s',
      s:{font:{bold:true,sz:14,color:{rgb:'1B6B3A'}},fill:{fgColor:{rgb:'E8F5E9'}}}};
    // Style section headers
    ['A5','A9'].forEach(ref=>{
      if(ws1[ref]) ws1[ref].s={font:{bold:true,sz:11,color:{rgb:'FFFFFF'}},fill:{fgColor:{rgb:'1B6B3A'}}};
    });
    // Style column headers row 6 and 10
    ['A6','B6','C6','D6','E6','A10','B10','C10','D10','E10','F10'].forEach(ref=>{
      if(ws1[ref]) ws1[ref].s={font:{bold:true,color:{rgb:'FFFFFF'}},fill:{fgColor:{rgb:'2E7D32'}}};
    });
    ws1['!merges']=[{s:{r:0,c:0},e:{r:0,c:5}},{s:{r:1,c:0},e:{r:1,c:5}},{s:{r:2,c:0},e:{r:2,c:5}}];
    XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

    // Sheet 2: Daily Breakdown
    const s2 = [
      ['PDIC Broadband - Daily Connection Breakdown'],
      [`Period: ${rangeFrom} to ${rangeTo}  |  Sub-District: ${subSuffix}`],
      [`Generated: ${new Date().toLocaleString('en-IN')}`],
      [],
      ['Sub-District', 'Date', 'New Connections', 'Retention', 'Renewals', 'Disconnected', 'Net Growth'],
    ];
    filteredSubs.forEach(s=>{
      s.daily.forEach(d=>{
        s2.push([
          s.sub,
          d.date,
          d.new_active||0,
          d.retention||0,
          d.renewal||0,
          d.left_active||0,
          d.net===null||d.net===undefined?'':d.net
        ]);
      });
      s2.push([]);
    });
    const ws2 = XLSX.utils.aoa_to_sheet(s2);
    ws2['!cols']=[{wch:22},{wch:14},{wch:18},{wch:15},{wch:13}];
    // Style title
    ws2['A1']={v:'PDIC Broadband - Daily Connection Breakdown',t:'s',
      s:{font:{bold:true,sz:14,color:{rgb:'1B6B3A'}},fill:{fgColor:{rgb:'E8F5E9'}}}};
    // Style header row (row 5)
    ['A5','B5','C5','D5','E5'].forEach(ref=>{
      if(ws2[ref]) ws2[ref].s={font:{bold:true,color:{rgb:'FFFFFF'}},fill:{fgColor:{rgb:'2E7D32'}}};
    });
    ws2['!merges']=[{s:{r:0,c:0},e:{r:0,c:4}},{s:{r:1,c:0},e:{r:1,c:4}},{s:{r:2,c:0},e:{r:2,c:4}}];
    XLSX.utils.book_append_sheet(wb, ws2, 'Daily Breakdown');

    XLSX.writeFile(wb, `PDIC-Connection-${subSuffix.replace(/ /g,'-')}-${rangeFrom}-${rangeTo}.xlsx`);
  };;

  const applyPreset = (preset) => {
    const now = new Date();
    let from, to = now.toISOString().split('T')[0];
    if(preset==='today') { from=to; }
    else if(preset==='yesterday') { 
      const y=new Date(now-86400000); 
      from=to=y.toISOString().split('T')[0]; 
    }
    else if(preset==='week') { 
      const d=new Date(now); d.setDate(d.getDate()-d.getDay()+1);
      from=d.toISOString().split('T')[0]; 
    }
    else if(preset==='last_week') {
      const d=new Date(now); d.setDate(d.getDate()-d.getDay()-6);
      const e=new Date(now); e.setDate(e.getDate()-e.getDay());
      from=d.toISOString().split('T')[0]; to=e.toISOString().split('T')[0];
    }
    else if(preset==='month') { 
      from=new Date(now.getFullYear(),now.getMonth(),1).toISOString().split('T')[0]; 
    }
    else if(preset==='last_month') {
      const f=new Date(now.getFullYear(),now.getMonth()-1,1);
      const t=new Date(now.getFullYear(),now.getMonth(),0);
      from=f.toISOString().split('T')[0]; to=t.toISOString().split('T')[0];
    }
    else if(preset==='quarter') {
      from=new Date(now.getFullYear(),Math.floor(now.getMonth()/3)*3,1).toISOString().split('T')[0];
    }
    setRangeFrom(from); setRangeTo(to); setRangePreset(preset);
    loadRange(from, to);
  };

  useEffect(() => {
    api.get('/reports/target-tracker').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => { if(activeTab==='range') loadRange(); }, [activeTab]);

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',gap:'12px',color:'#94a3b8'}}>
      <div style={{width:'32px',height:'32px',border:`2px solid ${primary}33`,borderTop:`2px solid ${primary}`,borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
      Loading...
    </div>
  );

  if (!data?.success) return <div style={{padding:'20px',color:'#ef4444'}}>Failed to load data.</div>;

  const { config } = data;
  const sub_districts = isSubDistAdmin ? (data.sub_districts||[]).filter(s=>s.sub===mySubCode) : (data.sub_districts||[]);
  // For subdist_admin use their own sub data, for others use overall
  const mySub = isSubDistAdmin ? sub_districts[0] : null;
  const overall = isSubDistAdmin && mySub ? {
    current_active: mySub.current_active,
    total_growth: mySub.growth,
    overall_pct: Math.round(mySub.growth/mySub.target_growth*100),
    daily_needed: mySub.daily_needed
  } : data.overall;
  const fmtN = n => Number(n||0).toLocaleString('en-IN');
  const fmtD = d => new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});
  const timePct = config.progress_pct;
  const getColor = pct => pct >= timePct ? '#16a34a' : pct >= timePct*0.7 ? '#d97706' : '#dc2626';
  const myTarget = isSubDistAdmin&&mySub ? mySub.target_active : config.target_active;
  const targetPct = Math.min(100,Math.round(overall.current_active/myTarget*100));

  const loadDaily = async (sub) => {
    if(dailyData[sub]) return;
    setDailyLoading(p=>({...p,[sub]:true}));
    try {
      const r = await api.get('/reports/target-daily', {params:{sub}});
      if(r.data.success) setDailyData(p=>({...p,[sub]:r.data}));
    } catch(e){}
    setDailyLoading(p=>({...p,[sub]:false}));
  };

  const toggleSub = sub => {
    const willExpand = !expandedSubs[sub];
    setExpandedSubs(p => ({...p,[sub]:!p[sub]}));
    if(willExpand) loadDaily(sub);
  };
  const allExpanded = sub_districts.every(s => expandedSubs[s.sub]);
  const toggleAll = () => {
    if(allExpanded) setExpandedSubs({});
    else { const a={}; sub_districts.forEach(s=>a[s.sub]=true); setExpandedSubs(a); }
  };

  return (
    <div style={{maxWidth:'1100px',fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif'}}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes slideDown{from{opacity:0;max-height:0}to{opacity:1;max-height:600px}}
        @media print{.no-print{display:none!important}.print-only{display:block!important}body{print-color-adjust:exact;-webkit-print-color-adjust:exact;margin:0;padding:0}#app-sidebar,#app-topbar{display:none!important}#app-layout>div:last-child{margin-left:0!important;padding-left:16px!important}}
      `}</style>

      {/* Tabs */}
      <div style={{display:'flex',gap:'8px',marginBottom:'20px'}} className="no-print">
        {[['target','🎯 Target Tracker'],['range','📊 Connection Report']].map(([t,l])=>(
          <button key={t} onClick={()=>setActiveTab(t)}
            style={{padding:'8px 18px',border:'none',borderRadius:'8px',cursor:'pointer',
              fontWeight:'700',fontSize:'13px',
              background:activeTab===t?primary:'#f1f5f9',
              color:activeTab===t?'white':'#64748b'}}>
            {l}
          </button>
        ))}
      </div>

      {/* Range Report Tab */}
      {activeTab==='range' && (
        <div>
          <div style={{background:'white',borderRadius:'12px',padding:'16px',marginBottom:'16px',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginBottom:'12px'}}>
              {[['today','Today'],['yesterday','Yesterday'],['week','This Week'],['last_week','Last Week'],['month','This Month'],['last_month','Last Month'],['quarter','This Quarter']].map(([p,l])=>(
                <button key={p} onClick={()=>applyPreset(p)}
                  style={{padding:'6px 12px',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'600',
                    background:rangePreset===p?primary:'#f1f5f9',color:rangePreset===p?'white':'#64748b'}}>
                  {l}
                </button>
              ))}
            </div>
            <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
              <input type="date" value={rangeFrom} onChange={e=>setRangeFrom(e.target.value)}
                style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'6px',fontSize:'13px'}}/>
              <span style={{color:'#94a3b8'}}>to</span>
              <input type="date" value={rangeTo} onChange={e=>setRangeTo(e.target.value)}
                style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'6px',fontSize:'13px'}}/>
              <button onClick={()=>loadRange()}
                style={{padding:'6px 16px',background:primary,color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'700',fontSize:'13px'}}>
                Load
              </button>
              {rangeData && (
                <select value={selectedSub} onChange={e=>setSelectedSub(e.target.value)}
                  style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'6px',fontSize:'13px'}}>
                  <option value="all">All Sub-Districts</option>
                  {rangeData.subs.map(s=>(
                    <option key={s.sub} value={s.sub}>{s.sub}</option>
                  ))}
                </select>
              )}
              {rangeData && (
                <button onClick={exportRangeExcel}
                  style={{padding:'6px 16px',background:'#16a34a',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'700',fontSize:'13px'}}>
                  📥 {selectedSub==='all'?'Download All':'Download '+selectedSub}
                </button>
              )}
            </div>
          </div>

          {rangeLoading && <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>Loading...</div>}

          {rangeData && !rangeLoading && (
            <div>
              {(()=>{
                const ss = selectedSub==='all'?rangeData.subs:rangeData.subs.filter(s=>s.sub===selectedSub);
                const sNew=ss.reduce((a,s)=>a+s.total_new,0);
                const sRet=ss.reduce((a,s)=>a+s.total_retention,0);
                const sRen=ss.reduce((a,s)=>a+s.total_renewal,0);
                const sDis=ss.reduce((a,s)=>a+s.daily.reduce((b,d)=>b+(d.left_active||0),0),0);
                const sStart=selectedSub==='all'?rangeData.overall.start_active:(ss[0]?.start_active||0);
                const sEnd=selectedSub==='all'?rangeData.overall.end_active:(ss[0]?.end_active||0);
                const sNet=selectedSub==='all'?rangeData.overall.net:ss.reduce((a,s)=>a+s.net,0);
                return (
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px',marginBottom:'16px'}}>
                {[
                  ['📊 Start Active',sStart,'#475569',false,null],
                  ['📊 End Active',sEnd,'#475569',false,null],
                  ['🆕 New Connections',sNew,'#16a34a',true,'new'],
                  ['🔄 Retention',sRet,'#0891b2',true,'retention'],
                  ['♻️ Renewals',sRen,'#7c3aed',true,'renewal'],
                  ['🔴 Disconnected',sDis,'#dc2626',true,'disconnected'],
                  ['📈 Net Growth',sNet,sNet>=0?'#2563eb':'#dc2626',true,null]
                ].map(([l,v,color,sign,ftype])=>(
                  <div key={l} onClick={()=>{ if(ftype){ setRangeTypeFilter(rangeTypeFilter===ftype?'all':ftype); if(ftype!==rangeTypeFilter) loadUsers(ftype,l,color); } }}
                    style={{background:ftype&&rangeTypeFilter===ftype?color+'11':'white',
                      borderRadius:'10px',padding:'16px',textAlign:'center',
                      boxShadow:ftype&&rangeTypeFilter===ftype?`0 0 0 2px ${color}`:'0 1px 4px rgba(0,0,0,0.06)',
                      cursor:ftype?'pointer':'default',transition:'all 0.15s'}}>
                    <div style={{fontSize:'11px',color:'#64748b',marginBottom:'4px'}}>{l}</div>
                    <div style={{fontSize:'24px',fontWeight:'900',color}}>{sign&&v>=0?'+':''}{fmtN(v)}</div>
                    {ftype&&<div style={{fontSize:'10px',color,marginTop:'4px'}}>{rangeTypeFilter===ftype?'✓ Filtered':'Click to filter'}</div>}
                  </div>
                ))}
              </div>
              );})()}
              <div style={{background:'white',borderRadius:'12px',overflow:'hidden',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead>
                    <tr style={{background:'#f8fafc'}}>
                      {['Sub-District','New','Retention','Renewals','Disconnected','Net','Details'].map((h,i)=>(
                        <th key={h} style={{padding:'10px 14px',textAlign:i===0?'left':'right',fontSize:'12px',color:'#64748b',fontWeight:'700',
                          ...(i===6?{textAlign:'center'}:{})}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rangeData.subs.filter(s=>selectedSub==='all'||s.sub===selectedSub).map((s,i)=>{
                      // Filter daily data based on type
                      const filteredDaily = rangeTypeFilter==='all' ? s.daily :
                        s.daily.map(d=>({...d,
                          new_active: rangeTypeFilter==='new'?d.new_active:0,
                          retention: rangeTypeFilter==='retention'?d.retention:0,
                          renewal: rangeTypeFilter==='renewal'?d.renewal:0,
                          left_active: rangeTypeFilter==='disconnected'?d.left_active:0,
                        })).filter(d=>d.new_active||d.retention||d.renewal||d.left_active||d.net!==null);
                      const filteredNew = rangeTypeFilter==='all'?s.total_new:rangeTypeFilter==='new'?s.total_new:0;
                      const filteredRetention = rangeTypeFilter==='all'?s.total_retention:rangeTypeFilter==='retention'?s.total_retention:0;
                      const filteredRenewal = rangeTypeFilter==='all'?s.total_renewal:rangeTypeFilter==='renewal'?s.total_renewal:0;
                      const filteredLeft = rangeTypeFilter==='all'?s.daily.reduce((a,d)=>a+(d.left_active||0),0):rangeTypeFilter==='disconnected'?s.daily.reduce((a,d)=>a+(d.left_active||0),0):0;
                      if(rangeTypeFilter!=='all'&&filteredNew===0&&filteredRetention===0&&filteredRenewal===0&&filteredLeft===0) return null;
                      return (
                      <React.Fragment key={s.sub}>
                        <tr style={{background:i%2?'#f9fafb':'white',cursor:'pointer'}}
                          onClick={()=>setExpandedRangeSub(p=>({...p,[s.sub]:!p[s.sub]}))}>
                          <td style={{padding:'10px 14px',fontWeight:'700',fontSize:'13px'}}>{s.sub}</td>
                          <td style={{padding:'10px 14px',textAlign:'right',color:'#16a34a',fontWeight:'700'}}>{filteredNew>0?'+'+fmtN(filteredNew):'—'}</td>
                          <td style={{padding:'10px 14px',textAlign:'right',color:'#0891b2',fontWeight:'700'}}>{filteredRetention>0?'+'+fmtN(filteredRetention):'—'}</td>
                          <td style={{padding:'10px 14px',textAlign:'right',color:'#7c3aed',fontWeight:'700'}}>{filteredRenewal>0?'+'+fmtN(filteredRenewal):'—'}</td>
                          <td style={{padding:'10px 14px',textAlign:'right',color:'#dc2626',fontWeight:'700'}}>
                            {filteredLeft>0?'-'+fmtN(filteredLeft):'—'}
                          </td>
                          <td style={{padding:'10px 14px',textAlign:'right',fontWeight:'800',
                            color:s.net>=0?'#2563eb':'#dc2626'}}>{s.net>=0?'+':''}{fmtN(s.net)}</td>
                          <td style={{padding:'10px 14px',textAlign:'center',fontSize:'12px',color:'#94a3b8'}}>
                            {expandedRangeSub[s.sub]?'▲':'▼'}
                          </td>
                        </tr>
                        {expandedRangeSub[s.sub] && (
                          <tr key={s.sub+'_detail'}>
                            <td colSpan={7} style={{padding:'0 14px 12px',background:'#f0f9ff'}}>
                              <table style={{width:'100%',borderCollapse:'collapse',marginTop:'8px'}}>
                                <thead>
                                  <tr style={{background:'#e0f2fe'}}>
                                    {['Date','New','Retention','Renewals','Disconnected','Net'].map(h=>(
                                      <th key={h} style={{padding:'6px 10px',textAlign:h==='Date'?'left':'right',fontSize:'11px',color:'#0369a1'}}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredDaily.map(d=>(
                                    <tr key={d.date}>
                                      <td style={{padding:'5px 10px',fontSize:'12px'}}>{fmtD(d.date+'T00:00:00')}</td>
                                      <td style={{padding:'5px 10px',textAlign:'right',fontSize:'12px',color:'#16a34a'}}>{d.new_active>0?'+'+d.new_active:'—'}</td>
                                      <td style={{padding:'5px 10px',textAlign:'right',fontSize:'12px',color:'#0891b2'}}>{d.retention>0?'+'+d.retention:'—'}</td>
                                      <td style={{padding:'5px 10px',textAlign:'right',fontSize:'12px',color:'#7c3aed'}}>{d.renewal>0?'+'+d.renewal:'—'}</td>
                                      <td style={{padding:'5px 10px',textAlign:'right',fontSize:'12px',color:'#dc2626'}}>{d.left_active>0?'-'+d.left_active:'—'}</td>
                                      <td style={{padding:'5px 10px',textAlign:'right',fontSize:'12px',fontWeight:'700',
                                        color:d.net===null?'#94a3b8':d.net>=0?'#2563eb':'#dc2626'}}>
                                        {d.net===null?'—':d.net>=0?'+'+fmtN(d.net):fmtN(d.net)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                      );})})
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* User Details Modal */}
      {userModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'flex-start',justifyContent:'flex-end'}}
          onClick={e=>e.target===e.currentTarget&&setUserModal(null)}>
          <div style={{background:'white',width:'min(680px,95vw)',height:'100vh',overflowY:'auto',boxShadow:'-4px 0 24px rgba(0,0,0,0.15)'}}>
            {/* Header */}
            <div style={{padding:'16px 20px',borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between',alignItems:'center',
              background:userModal.color+'11',position:'sticky',top:0,zIndex:10}}>
              <div>
                <div style={{fontWeight:'800',fontSize:'15px',color:userModal.color}}>{userModal.label}</div>
                <div style={{fontSize:'12px',color:'#64748b',marginTop:'2px'}}>{rangeFrom} to {rangeTo} · {selectedSub==='all'?'All Sub-Districts':selectedSub}</div>
              </div>
              <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                <span style={{background:userModal.color,color:'white',borderRadius:'20px',padding:'2px 10px',fontSize:'12px',fontWeight:'700'}}>
                  {userList.length} users
                </span>
                <button onClick={()=>setUserModal(null)}
                  style={{border:'none',background:'#f1f5f9',borderRadius:'8px',padding:'6px 10px',cursor:'pointer',fontSize:'16px'}}>✕</button>
              </div>
            </div>
            {/* Search */}
            <div style={{padding:'12px 20px',borderBottom:'1px solid #f1f5f9',position:'sticky',top:'57px',background:'white',zIndex:9}}>
              <input placeholder="🔍 Search by name, username, mobile, LCO..." value={userSearch}
                onChange={e=>setUserSearch(e.target.value)}
                style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',outline:'none'}}/>
            </div>
            {/* User List */}
            <div style={{padding:'8px 0'}}>
              {userLoading && <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>Loading...</div>}
              {!userLoading && userList.filter(u=>{
                if(!userSearch) return true;
                const s=userSearch.toLowerCase();
                return (u.name||'').toLowerCase().includes(s)||(u.username||'').toLowerCase().includes(s)||
                  (u.mobile||'').includes(s)||(u.lco_code||'').toLowerCase().includes(s);
              }).map((u,i)=>(
                <div key={u.username+i} style={{padding:'12px 20px',borderBottom:'1px solid #f8fafc',
                  background:i%2?'#fafafa':'white'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                    <div>
                      <div style={{fontWeight:'700',fontSize:'13px',color:'#0f172a'}}>{u.name||u.username}</div>
                      <div style={{fontSize:'11px',color:'#64748b',marginTop:'2px'}}>
                        👤 {u.username} · 📞 {u.mobile||'—'} · 🏢 {u.lco_code||'—'}
                      </div>
                      <div style={{fontSize:'11px',color:'#94a3b8',marginTop:'2px'}}>
                        📡 {u.plan||'—'} · 🗓 {u.date} · 📍 {u.sub_dist_code||'—'}
                      </div>
                    </div>
                    <div style={{textAlign:'right',flexShrink:0,marginLeft:'12px'}}>
                      <span style={{background:userModal.color+'22',color:userModal.color,
                        borderRadius:'6px',padding:'2px 8px',fontSize:'11px',fontWeight:'700'}}>
                        {u.prev_status} → {u.new_status}
                      </span>
                    </div>
                  </div>
                  {u.address&&<div style={{fontSize:'10px',color:'#94a3b8',marginTop:'4px'}}>📍 {u.address}</div>}
                </div>
              ))}
              {!userLoading && userList.length===0 && (
                <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>No data found</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Original Target Tracker */}
      {activeTab==='target' && <div>

      {/* Clean Header */}
      <div style={{marginBottom:'24px'}} className="no-print">
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
          <div>
            <div style={{fontSize:'11px',fontWeight:'700',color:primary,letterSpacing:'1.5px',textTransform:'uppercase',marginBottom:'4px'}}>PDIC · Q4 2026</div>
            <h1 style={{margin:'0 0 4px',fontSize:'24px',fontWeight:'800',color:'#0f172a',letterSpacing:'-0.5px'}}>{isSubDistAdmin?mySubCode+' — Target Tracker':'220K Target Tracker'}</h1>
            <p style={{margin:0,color:'#94a3b8',fontSize:'13px'}}>{fmtD(config.start_date)} — {fmtD(config.end_date)} &nbsp;·&nbsp; {config.days_remaining} days remaining</p>
          </div>
          <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
            {!isSubDistAdmin && <button onClick={toggleAll} style={{padding:'8px 14px',background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600',color:'#475569'}}>
              {allExpanded?'⊖ Collapse All':'⊕ Expand All'}
            </button>}
            <button onClick={()=>{
              // Excel export
              const rows = [['PDIC - 220K Target Tracker - '+new Date().toLocaleDateString('en-IN')]];
              rows.push(['']);
              rows.push(['Sub-District','Baseline Active','Current Active','Net Growth','Target Growth','Achieved %','Target Active','Remaining','Daily Needed','Share %']);
              sub_districts.forEach(s=>{
                const pct = Math.max(0,Math.round(s.growth/s.target_growth*100));
                rows.push([s.sub,s.baseline_active,s.current_active,s.growth>=0?'+'+s.growth:s.growth,'+'+s.target_growth,pct+'%',s.target_active,Math.max(0,s.target_active-s.current_active),s.daily_needed>0?'+'+s.daily_needed:'Done',s.share_pct+'%']);
              });
              rows.push(['']);
              rows.push(['TOTAL',config.baseline_active,overall.current_active,overall.total_growth>=0?'+'+overall.total_growth:overall.total_growth,'+'+config.growth_needed,Math.max(0,overall.overall_pct)+'%',config.target_active,Math.max(0,config.target_active-overall.current_active),'+'+overall.daily_needed,'100%']);
              rows.push(['']);
              rows.push(['Campaign: Oct 1 2026 - Mar 31 2027']);
              rows.push(['Days Remaining: '+config.days_remaining]);
              rows.push(['Target: '+config.target_active]);
              const csv = rows.map(r=>r.join(',')).join('\n');
              const blob = new Blob([csv],{type:'text/csv'});
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = 'target_tracker_'+new Date().toISOString().split('T')[0]+'.csv';
              a.click();
            }} style={{padding:'8px 14px',background:'#16a34a',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600',color:'white'}}>
              📥 Excel
            </button>
            <button onClick={handlePrint} style={{padding:'8px 14px',background:primary,border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600',color:'white'}}>
              🖨️ Print
            </button>
          </div>
        </div>
      </div>

      {/* Print Header */}
      <div style={{display:'none'}} className="print-only">
        <div style={{borderBottom:`3px solid ${primary}`,paddingBottom:'12px',marginBottom:'20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <div style={{fontSize:'18px',fontWeight:'800',color:'#0f172a'}}>PDIC — 220K Target Tracker · 2026-27</div>
            <div style={{fontSize:'12px',color:'#64748b',marginTop:'4px'}}>{fmtD(config.start_date)} to {fmtD(config.end_date)} · Generated: {new Date().toLocaleString('en-IN')}</div>
          </div>
          <div style={{textAlign:'right'}}>
            <div style={{fontSize:'32px',fontWeight:'900',color:primary}}>{targetPct}%</div>
            <div style={{fontSize:'11px',color:'#64748b'}}>of 220K reached</div>
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'12px',marginBottom:'20px'}}>
        {[
          {label:'Current Active',value:fmtN(overall.current_active),note:'Baseline: '+fmtN(config.baseline_active),color:'#0f172a',border:'#e2e8f0'},
          {label:'Campaign Growth',value:(overall.total_growth>=0?'+ ':'- ')+fmtN(Math.abs(overall.total_growth)),note:Math.max(0,overall.overall_pct)+'% of '+fmtN(isSubDistAdmin&&mySub?mySub.target_growth:config.growth_needed)+' needed',color:overall.total_growth>=0?'#16a34a':'#dc2626',border:overall.total_growth>=0?'#bbf7d0':'#fecaca'},
          {label:'Still Needed',value:fmtN(Math.max(0,(isSubDistAdmin&&mySub?mySub.target_active:config.target_active)-overall.current_active)),note:'Need +'+overall.daily_needed+' per day',color:'#d97706',border:'#fde68a'},
          {label:'Days Left',value:config.days_remaining,note:timePct+'% of campaign elapsed',color:'#7c3aed',border:'#ddd6fe'},
          {label:"Today's Change",value:(data.today_change?.net>=0?'+ ':'- ')+fmtN(Math.abs(data.today_change?.net||0)),note:'Click for sub-wise breakdown',color:data.today_change?.net>=0?'#0891b2':'#dc2626',border:data.today_change?.net>=0?'#bae6fd':'#fecaca',onClick:()=>setShowToday(s=>!s)},
        ].map((k,i)=>(
          <div key={i} onClick={k.onClick} style={{background:'white',borderRadius:'12px',padding:'18px 20px',border:`1px solid ${k.border}`,boxShadow:'0 1px 3px rgba(0,0,0,0.04)',cursor:k.onClick?'pointer':'default',transition:'box-shadow 0.2s'}}
            onMouseEnter={e=>{if(k.onClick)e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.1)'}}
            onMouseLeave={e=>{if(k.onClick)e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.04)'}}>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#94a3b8',letterSpacing:'0.5px',textTransform:'uppercase',marginBottom:'8px'}}>{k.label}</div>
            <div style={{fontSize:'26px',fontWeight:'800',color:k.color,letterSpacing:'-0.5px',lineHeight:1}}>{k.value}</div>
            <div style={{fontSize:'11px',color:'#94a3b8',marginTop:'6px'}}>{k.note}</div>
          </div>
        ))}
      </div>

      {/* Today's Change Panel */}
      {showToday && data.today_change && (
        <div style={{background:'white',borderRadius:'12px',border:'1px solid #bae6fd',padding:'16px 20px',marginBottom:'16px',boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
            <div>
              <span style={{fontWeight:'700',fontSize:'13px',color:'#0f172a'}}>📊 Today's Activity</span>
              <span style={{marginLeft:'8px',fontSize:'12px',color:'#94a3b8'}}>{data.today_change.date}</span>
            </div>
            <div style={{fontSize:'12px',color:'#64748b'}}>
              Yesterday: <strong>{fmtN(data.today_change.yesterday_active)}</strong> → Today: <strong style={{color:'#16a34a'}}>{fmtN(data.today_change.today_active)}</strong> = <strong style={{color:data.today_change.net>=0?'#16a34a':'#dc2626'}}>{data.today_change.net>=0?'+':''}{fmtN(data.today_change.net)}</strong>
            </div>
          </div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
            <thead>
              <tr style={{background:'#f8fafc'}}>
                {['Sub-District','New Active','Left Active','Net Change','Share of Today'].map(h=>(
                  <th key={h} style={{padding:'8px 12px',textAlign:'left',fontWeight:'700',color:'#64748b',borderBottom:'1px solid #e2e8f0'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.today_change.by_sub||[]).map((s,i)=>(
                <tr key={i} style={{borderBottom:'1px solid #f1f5f9',background:i%2?'#f8fafc':'white'}}>
                  <td style={{padding:'8px 12px',fontWeight:'700',color:'#0f172a'}}>{s.sub}</td>
                  <td style={{padding:'8px 12px',color:'#16a34a',fontWeight:'700'}}>+{fmtN(s.new_active)}</td>
                  <td style={{padding:'8px 12px',color:s.left_active>0?'#dc2626':'#94a3b8'}}>{s.left_active>0?'-'+fmtN(s.left_active):'—'}</td>
                  <td style={{padding:'8px 12px',fontWeight:'800',color:s.net>=0?'#16a34a':'#dc2626'}}>{s.net>=0?'+':''}{fmtN(s.net)}</td>
                  <td style={{padding:'8px 12px',color:'#64748b'}}>{data.today_change.net>0?Math.round(s.net/data.today_change.net*100):0}%</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{background:'#f0f9ff',borderTop:'2px solid #bae6fd',fontWeight:'800'}}>
                <td style={{padding:'8px 12px',color:'#0f172a'}}>TOTAL</td>
                <td style={{padding:'8px 12px',color:'#16a34a'}}>+{fmtN((data.today_change.by_sub||[]).reduce((a,s)=>a+s.new_active,0))}</td>
                <td style={{padding:'8px 12px',color:'#dc2626'}}>{(data.today_change.by_sub||[]).reduce((a,s)=>a+s.left_active,0)>0?'-'+fmtN((data.today_change.by_sub||[]).reduce((a,s)=>a+s.left_active,0)):'—'}</td>
                <td style={{padding:'8px 12px',color:data.today_change.net>=0?'#16a34a':'#dc2626'}}>{data.today_change.net>=0?'+':''}{fmtN(data.today_change.net)}</td>
                <td style={{padding:'8px 12px',color:'#64748b'}}>100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Overall Progress */}
      <div style={{background:'white',borderRadius:'12px',padding:'20px 24px',border:'1px solid #e2e8f0',marginBottom:'20px',boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'14px'}}>
          <span style={{fontSize:'13px',fontWeight:'700',color:'#0f172a'}}>Overall Progress</span>
          <span style={{fontSize:'12px',color:'#94a3b8'}}>{fmtN(overall.current_active)} of {fmtN(config.target_active)} target</span>
        </div>
        <div style={{position:'relative',background:'#f1f5f9',borderRadius:'99px',height:'20px',overflow:'visible',marginBottom:'6px'}}>
          <div style={{position:'absolute',left:`${timePct}%`,top:'-4px',bottom:'-4px',width:'2px',background:'#94a3b8',zIndex:2,borderRadius:'1px'}}/>
          <div style={{position:'absolute',left:`${timePct}%`,top:'-18px',transform:'translateX(-50%)',fontSize:'9px',color:'#94a3b8',fontWeight:'600',whiteSpace:'nowrap'}}>TODAY</div>
          <div style={{height:'100%',width:`${targetPct}%`,background:`linear-gradient(90deg,${primary},#16a34a)`,borderRadius:'99px',transition:'width 1s ease',display:'flex',alignItems:'center',justifyContent:'flex-end',paddingRight:'10px',boxSizing:'border-box'}}>
            {targetPct > 10 && <span style={{color:'white',fontSize:'11px',fontWeight:'700'}}>{targetPct}%</span>}
          </div>
        </div>
        <div style={{display:'flex',justifyContent:'space-between',fontSize:'11px',color:'#94a3b8',marginTop:'4px'}}>
          <span>Baseline {fmtN(isSubDistAdmin&&mySub?mySub.baseline_active:config.baseline_active)}</span>
          <span>Target {isSubDistAdmin&&mySub?fmtN(mySub.target_active):'220,842'}</span>
        </div>

      </div>

      {/* Sub-District List */}
      <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
        {sub_districts.map((s,i)=>{
          const pct=Math.max(0,Math.round(s.growth/s.target_growth*100));
          const color=getColor(pct);
          const isExp=expandedSubs[s.sub];
          const onTrack=s.growth>=Math.round(s.target_growth*timePct/100);
          const remaining=Math.max(0,s.target_active-s.current_active);
          return (
            <div key={i} style={{background:'white',borderRadius:'12px',border:'1px solid #e2e8f0',overflow:'hidden',boxShadow:'0 1px 3px rgba(0,0,0,0.04)',transition:'box-shadow 0.2s'}}>
              {/* Click Row */}
              <div onClick={()=>toggleSub(s.sub)} style={{padding:'14px 20px',cursor:'pointer',display:'flex',alignItems:'center',gap:'16px',userSelect:'none'}}>
                <span style={{color:'#cbd5e1',fontSize:'12px',transition:'transform 0.2s',transform:isExp?'rotate(90deg)':'none',display:'inline-block',minWidth:'12px'}}>▶</span>
                
                {/* Name + badge */}
                <div style={{minWidth:'90px'}}>
                  <div style={{fontWeight:'700',fontSize:'14px',color:'#0f172a'}}>{s.sub}</div>
                  <span style={{fontSize:'10px',fontWeight:'700',color:color}}>{onTrack?'✓ On Track':'! Needs Push'}</span>
                </div>

                {/* Progress bar */}
                <div style={{flex:1,minWidth:'120px'}}>
                  <div style={{background:'#f1f5f9',borderRadius:'99px',height:'8px',overflow:'hidden',position:'relative'}}>
                    <div style={{position:'absolute',left:`${timePct}%`,top:0,bottom:0,width:'1px',background:'#94a3b8'}}/>
                    <div style={{width:`${Math.min(100,pct)}%`,height:'100%',background:color,borderRadius:'99px',transition:'width 0.5s'}}/>
                  </div>
                </div>
                <span style={{fontSize:'13px',fontWeight:'800',color:color,minWidth:'40px',textAlign:'right'}}>{pct}%</span>

                {/* Key numbers */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'0',minWidth:'480px',borderLeft:'1px solid #f1f5f9',paddingLeft:'16px'}}>
                  {[
                    ['Baseline',fmtN(s.baseline_active),'#64748b'],
                    ['Active',fmtN(s.current_active),'#16a34a'],
                    ['Growth',(s.growth>=0?'+':'')+fmtN(s.growth),s.growth>=0?'#16a34a':'#dc2626'],
                    ['Target Growth','+'+fmtN(s.target_growth),'#2563eb'],
                    ['Target Active',fmtN(s.target_active),'#7c3aed'],
                  ].map(([l,v,c],j)=>(
                    <div key={j} style={{textAlign:'center',padding:'0 8px',borderRight:j<4?'1px solid #f1f5f9':'none'}}>
                      <div style={{fontSize:'14px',fontWeight:'800',color:c}}>{v}</div>
                      <div style={{fontSize:'9px',color:'#94a3b8',marginTop:'2px',textTransform:'uppercase',letterSpacing:'0.3px'}}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Expanded */}
              {isExp && (
                <div style={{borderTop:'1px solid #f8fafc',padding:'16px 20px',background:'#fafafa',animation:'slideDown 0.2s ease'}}>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:'10px'}}>
                    {[
                      {l:'Total Connections',v:fmtN(s.current_total),c:'#0f172a',bg:'white'},
                      {l:'Active',v:fmtN(s.current_active),c:'#16a34a',bg:'#f0fdf4'},
                      {l:'Suspended',v:fmtN(s.current_suspended||0),c:'#dc2626',bg:'#fef2f2'},
                      {l:'Remaining to Target',v:fmtN(remaining),c:'#d97706',bg:'#fffbeb'},
                      {l:'Daily New Needed',v:s.daily_needed>0?'+'+s.daily_needed:'Done ✓',c:s.daily_needed>5?'#dc2626':'#16a34a',bg:s.daily_needed>5?'#fef2f2':'#f0fdf4'},
                      {l:'Share of Campaign',v:s.share_pct+'%',c:'#7c3aed',bg:'#faf5ff'},
                    ].map((x,j)=>(
                      <div key={j} style={{background:x.bg,borderRadius:'8px',padding:'12px',textAlign:'center',border:'1px solid #e2e8f0'}}>
                        <div style={{fontSize:'18px',fontWeight:'800',color:x.c}}>{x.v}</div>
                        <div style={{fontSize:'9px',color:'#94a3b8',marginTop:'3px',textTransform:'uppercase',letterSpacing:'0.5px',fontWeight:'600'}}>{x.l}</div>
                      </div>
                    ))}
                  </div>
                  {/* Daily Breakdown */}
                  <div style={{marginTop:'14px',borderTop:'1px solid #e2e8f0',paddingTop:'14px'}}>
                    <div style={{fontWeight:'700',fontSize:'12px',color:'#0f172a',marginBottom:'8px',display:'flex',justifyContent:'space-between'}}>
                      📅 Day-by-Day Progress
                      <span style={{fontSize:'11px',color:'#94a3b8',fontWeight:'400'}}>From Sep 27, 2026</span>
                    </div>
                    {dailyLoading[s.sub] ? (
                      <div style={{textAlign:'center',padding:'12px',color:'#94a3b8',fontSize:'12px'}}>Loading...</div>
                    ) : dailyData[s.sub] ? (
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                        <thead><tr style={{background:'#f8fafc'}}>
                          {['Day','Date','Active','Daily Change','Cumulative Growth','Progress'].map(h=>(
                            <th key={h} style={{padding:'6px 10px',textAlign:'left',fontWeight:'700',color:'#64748b',borderBottom:'1px solid #e2e8f0',whiteSpace:'nowrap'}}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {(dailyData[s.sub].days||[]).map((d,i)=>{
                            const nc=d.net_change;
                            const isBase=d.is_baseline;
                            const ncColor=nc>0?'#16a34a':nc<0?'#dc2626':'#94a3b8';
                            const pColor=d.target_progress>=timePct?'#16a34a':d.target_progress>=timePct*0.7?'#d97706':'#dc2626';
                            return(
                              <tr key={i} style={{borderBottom:'1px solid #f1f5f9',background:isBase?'#eff6ff':i%2?'#f8fafc':'white',fontStyle:isBase?'italic':'normal'}}>
                                <td style={{padding:'6px 10px',fontWeight:'700',color:isBase?'#2563eb':'#64748b'}}>{d.day}</td>
                                <td style={{padding:'6px 10px',color:'#64748b'}}>{new Date(d.date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</td>
                                <td style={{padding:'6px 10px',fontWeight:'600'}}>{fmtN(d.active)}</td>
                                <td style={{padding:'6px 10px',fontWeight:'700',color:ncColor}}>{nc>0?'+':''}{fmtN(nc)}</td>
                                <td style={{padding:'6px 10px',fontWeight:'700',color:d.cumulative_growth>=0?'#16a34a':'#dc2626'}}>{d.cumulative_growth>=0?'+':''}{fmtN(d.cumulative_growth)}</td>
                                {isBase ? <td style={{padding:'6px 10px',color:'#94a3b8',fontSize:'11px'}}>Starting point</td> : <td style={{padding:'6px 10px'}}><div style={{display:'flex',alignItems:'center',gap:'6px'}}><div style={{flex:1,background:'#f1f5f9',borderRadius:'20px',height:'6px',overflow:'hidden'}}><div style={{width:Math.min(100,Math.max(0,d.target_progress))+'%',height:'100%',background:pColor,borderRadius:'20px'}}/></div><span style={{fontSize:'10px',fontWeight:'700',color:pColor,minWidth:'28px'}}>{d.target_progress}%</span></div></td>}
                              </tr>
                            );
                          })}
                          {(dailyData[s.sub].days||[]).length===0&&<tr><td colSpan="6" style={{padding:'12px',textAlign:'center',color:'#94a3b8'}}>No daily data yet</td></tr>}
                        </tbody>
                      </table>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{marginTop:'16px',display:'flex',justifyContent:'space-between',fontSize:'11px',color:'#cbd5e1',padding:'0 4px'}}>
        <span>Baseline: {fmtD(config.start_date)} · Excludes KB13N & unassigned</span>
        <span>Live BMS sync · {new Date().toLocaleString('en-IN')}</span>
      </div>
    </div>}
    </div>
  );
};

export default TargetTracker;
